import { RowCustomPropertyService } from "./RowCustomPropertyService";
import { CustomPropertyRepository } from "./CustomPropertyRepository";
import { CustomPropertyModel } from "./CustomPropertyModel";
import { IRowCustomProperty } from "common/interfaces";
import { TDocumentReference } from "common/types/FirebaseTypes";
import { FirebaseDocument } from "common/models/FirebaseDocument";

class Service {
  async syncCustomProperties(
    ref: TDocumentReference,
    rowCustomProperties: IRowCustomProperty[]
  ): Promise<{
    updatedProps: FirebaseDocument<CustomPropertyModel>[];
    deletedPropsIds: string[];
  }> {
    const properties = await this.getAllWBSCustomProperties(ref);
    const rowPropsToUpdate = [...rowCustomProperties];

    const updatedProps = await this.applyUpdatedCustomProperties(
      ref,
      properties,
      rowPropsToUpdate
    );
    const newedProps = await this.applyCreatedCustomProperties(
      ref,
      properties,
      rowPropsToUpdate
    );
    const deletedProps = await this.applyDeletedCustomProperties(
      ref,
      properties,
      rowPropsToUpdate
    );

    // Atualizar o id das novas props
    for (let prop of newedProps) {
      const property = rowPropsToUpdate.find(
        (p) => p.label === prop.data.label
      );
      if (property) {
        property.id = prop.id;
      }
    }

    return {
      updatedProps: [...newedProps, ...updatedProps],
      deletedPropsIds: deletedProps.map((p) => p.id),
    };
  }

  private async applyCreatedCustomProperties(
    ref: TDocumentReference,
    currentProperties: FirebaseDocument<CustomPropertyModel>[],
    updatedProperties: IRowCustomProperty[]
  ): Promise<FirebaseDocument<CustomPropertyModel>[]> {
    const ids = currentProperties.map((prop) => prop.id);
    let newedProps = Array<FirebaseDocument<CustomPropertyModel>>();

    const listToAdd = updatedProperties
      .filter((prop) => !ids.includes(prop.id))
      .map((prop) => new CustomPropertyModel(prop.label));

    for (const newProp of listToAdd) {
      if (!newProp.label) continue;

      const newDoc = await this.createWBSCustomProperty(ref, newProp);
      if (!newDoc) continue;
      newedProps.push(newDoc);
    }

    return newedProps;
  }

  private async applyDeletedCustomProperties(
    ref: TDocumentReference,
    customProperties: FirebaseDocument<CustomPropertyModel>[],
    updatedProperties: IRowCustomProperty[]
  ): Promise<FirebaseDocument<CustomPropertyModel>[]> {
    const ids = updatedProperties.map((prop) => prop.id);
    let deletedProps = Array<FirebaseDocument<CustomPropertyModel>>();

    const listToDelete = customProperties.filter(
      (prop) => !ids.includes(prop.id)
    );

    for (const prop of listToDelete) {
      await this.deleteWBSCustomProperty(ref, prop.id);
      deletedProps.push(prop);
    }

    return deletedProps;
  }

  private async applyUpdatedCustomProperties(
    ref: TDocumentReference,
    currentProperties: FirebaseDocument<CustomPropertyModel>[],
    updatedProperties: IRowCustomProperty[]
  ): Promise<FirebaseDocument<CustomPropertyModel>[]> {
    let updatedProps: FirebaseDocument<CustomPropertyModel>[] = [];

    for (const prop of currentProperties) {
      const newProp = RowCustomPropertyService.findRowPropertyById(
        updatedProperties,
        prop.id
      );
      if (!newProp || !newProp.label) continue;

      if (prop.data.label !== newProp.label) {
        prop.data.label = newProp.label;
        await this.updateWBSCustomProperty(ref, prop);
      }
      updatedProps.push(prop);
    }

    return updatedProps;
  }

  async createWBSCustomProperty(
    ref: TDocumentReference,
    data: CustomPropertyModel
  ): Promise<FirebaseDocument<CustomPropertyModel>> {
    if (!ref || !data || !data.label)
      throw new Error("Invalid reference or custom property!");
    return CustomPropertyRepository.createProperty(ref, data);
  }

  async deleteWBSCustomProperty(
    ref: TDocumentReference,
    id: string
  ): Promise<void> {
    if (!ref || !id) return;
    return CustomPropertyRepository.deleteProperty(ref, id);
  }

  findWBSPropertyByLabel(
    customProperties: FirebaseDocument<CustomPropertyModel>[],
    label: string
  ): FirebaseDocument<CustomPropertyModel> | undefined {
    return customProperties.find((prop) =>
      prop.data ? prop.data.label.toLowerCase() === label.toLowerCase() : false
    );
  }

  async getWBSCustomPropertyByLabel(
    ref: TDocumentReference,
    label: string
  ): Promise<FirebaseDocument<CustomPropertyModel>> {
    if (!ref) throw new Error("Invalid document reference!");
    return CustomPropertyRepository.getPropertyByLabel(ref, label);
  }

  async getAllWBSCustomProperties(
    ref: TDocumentReference
  ): Promise<FirebaseDocument<CustomPropertyModel>[]> {
    if (!ref) return [];
    return CustomPropertyRepository.getAllProperties(ref);
  }

  mergePropertiesAndValues(
    customProperties: FirebaseDocument<CustomPropertyModel>[],
    rowPropertiesValues?: { [id: string]: string }
  ): {
    id: string;
    label: string;
    value: string;
  }[] {
    return customProperties.map((prop) => {
      let id = prop.id;
      let label = prop.data.label;
      let value =
        rowPropertiesValues && rowPropertiesValues[id]
          ? rowPropertiesValues[id]
          : "";

      return {
        id,
        label,
        value,
      };
    });
  }

  async updateWBSCustomProperty(
    ref: TDocumentReference,
    doc: FirebaseDocument<CustomPropertyModel>
  ): Promise<void> {
    if (!ref || !doc || !doc.id)
      throw new Error("Invalid reference or document data!");
    if (!doc.data.label) return;

    return CustomPropertyRepository.updateProperty(ref, doc);
  }
}

export const CustomPropertyService = new Service();



// WEBPACK FOOTER //
// ./src/modules/customProperties/CustomPropertyService.ts